import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	InputLabel,
	Stack,
	Checkbox
} from '@mui/material';
import ReactHookFormTextField from '../../components/ReactHookForm/ReactHookFormTextField';
import ReactHookFormCheckbox from '../../components/ReactHookForm/ReactHookFormCheckbox';
import { useForm } from 'react-hook-form';
import { ApolloError, useMutation } from '@apollo/client';
import { EnterGroupSignUpCode, EnterGroupSignUpCodeVariables } from '../../apollo/generated/types/EnterGroupSignUpCode';
import { CreateCheckoutSession } from '../../apollo/generated/types/CreateCheckoutSession';
import { ENTER_GROUP_SIGN_UP_CODE, CREATE_CHECKOUT_SESSION } from './MembershipOptionsPage.graphql';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useAuth0 } from '@auth0/auth0-react';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack5';
import privacyPDF from '../../../src/assets/files/9-hi Privacy.pdf';
import termsPDF from '../../../src/assets/files/9-hi Terms.pdf';
import { pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

interface FormFields {
	code: string;
	privacyAccepted: boolean;
	termsAccepted: boolean;
}

const MembershipOptionsPage = () => {
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [privacyRead, setPrivacyRead] = useState<boolean>(false);
	const [termsRead, setTermsRead] = useState<boolean>(false);
	const [privacyPageNum, setPrivacyPageNum] = useState<number>();
	const [termsPageNum, setTermsPageNum] = useState<number>();
	const { logout } = useAuth0();

	const { control, watch, handleSubmit } = useForm<FormFields>({ defaultValues: { code: '', privacyAccepted: false, termsAccepted: false } });
	const code = watch('code');
	const privacyAccepted = watch('privacyAccepted');
	const termsAccepted = watch('termsAccepted');

	const [enterSignUpCode, { loading: signUpLoading }] = useMutation<EnterGroupSignUpCode, EnterGroupSignUpCodeVariables>(ENTER_GROUP_SIGN_UP_CODE);
	const submit = (data: FormFields) => {
		enterSignUpCode({ variables: { signUpCode: data.code } })
			.then(() => window.location.reload())
			.catch((e: ApolloError) => {
				setErrorMessage(e.message);
				setShowErrorModal(true);
			});
	};

	const [createCheckoutSession, { loading: checkoutLoading }] = useMutation<CreateCheckoutSession>(CREATE_CHECKOUT_SESSION, {
		onCompleted: (data) => {
			window.location.replace(data.createCheckoutSession);
		},
		onError: (e) => {
			setErrorMessage(e.message);
			setShowErrorModal(true);
		},
	});

	const handlePrivacyScroll = (e: React.UIEvent<HTMLElement>) => {
		const bottom = e.currentTarget.scrollHeight - Math.ceil(e.currentTarget.scrollTop) <= e.currentTarget.clientHeight;
		if (bottom) {
			setPrivacyRead(true);
		}
	};

	const handleTermsScroll = (e: React.UIEvent<HTMLElement>) => {
		const bottom = e.currentTarget.scrollHeight - Math.ceil(e.currentTarget.scrollTop) <= e.currentTarget.clientHeight;
		if (bottom) {
			setTermsRead(true);
		}
	};

	const onPrivacyLoadSuccess = ({ numPages }) => {
		setPrivacyRead(false);
		setPrivacyPageNum(numPages);
	};

	const onTermsLoadSuccess = ({ numPages }) => {
		setTermsRead(false);
		setTermsPageNum(numPages);
	};

	let privacyPages: JSX.Element[] = [];
	let termsPages: JSX.Element[] = [];

	if (privacyPageNum) {
		for (let i = 0; i < privacyPageNum; ++i) {
			privacyPages.push(<Page key={i + 1} pageNumber={i + 1} />);
		}
	}

	if (termsPageNum) {
			for (let i = 0; i < termsPageNum; ++i) {
				termsPages.push(<Page key={i + 1} pageNumber={i + 1} />);
			}
		}



	return (
		<Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 ,  }}>
			<Card sx={{ minWidth: '700px' }}>
				<CardHeader title="Sign Up for a 9Hi™ Subscription" />
				<CardContent >
					<Stack gap={2} >
						<Box
																sx={{
																	maxHeight: '100px',
																	overflowX: 'hidden',
																	overflowY: 'auto',
																}}
																onScroll={handlePrivacyScroll} >
																<Document file={privacyPDF} onLoadSuccess={onPrivacyLoadSuccess}>
																	{privacyPages}
																</Document>
															</Box>

															<Stack direction="row" justifyContent="space-between">
						<ReactHookFormCheckbox name="privacyAccepted" label="I have read and agree to the Privacy Policy." control={control} readOnly={!privacyRead}></ReactHookFormCheckbox>
						<Button

																	variant="contained"
																	component="a"
																	download="9-hi Privacy.pdf"
																	href={require('../../../src/assets/files/9-hi Privacy.pdf')}
																	target="_blank">
																	Download
																</Button>
																</Stack>
						<Box
																sx={{
																	maxHeight: '100px',
																	overflowX: 'hidden',
																	overflowY: 'auto',
																}}
																onScroll={handleTermsScroll} >
																<Document file={termsPDF} onLoadSuccess={onTermsLoadSuccess} >
																	{termsPages}
																</Document>
															</Box>
															<Stack direction="row" justifyContent="space-between">
						<ReactHookFormCheckbox name="termsAccepted" label="I have read and agree to the Terms and Conditions." control={control} readOnly={!termsRead}></ReactHookFormCheckbox>
						<Button

																	variant="contained"
																	component="a"
																	download="9-hi Terms.pdf"
																	href={require('../../../src/assets/files/9-hi Terms.pdf')}
																	target="_blank">
																	Download
																</Button>
						</Stack>
						<LoadingButton variant="contained" disabled={signUpLoading || !(termsAccepted && privacyAccepted)} loading={checkoutLoading} onClick={() => createCheckoutSession()}>
							Sign Up with Stripe
						</LoadingButton>
						<Divider>or</Divider>
						<Box>
							<InputLabel>Enter a Group Sign-Up Code</InputLabel>
							<ReactHookFormTextField control={control} name="code" fullWidth regex={/^[a-zA-Z0-9]*$/} />
						</Box>
						<LoadingButton loading={signUpLoading} variant="contained" disabled={!code || checkoutLoading || !(termsAccepted && privacyAccepted)} onClick={handleSubmit(submit)}>
							Submit
						</LoadingButton>
						<Divider>or</Divider>
						<Button variant="outlined" onClick={() => logout({ returnTo: window.location.origin })} disabled={signUpLoading || checkoutLoading}>
							Sign Out
						</Button>
					</Stack>
				</CardContent>
			</Card>
			<Dialog open={showErrorModal}>
				<DialogTitle>Error Signing Up</DialogTitle>
				<DialogContent>
					<Alert severity="error">{errorMessage}</Alert>
				</DialogContent>
				<DialogActions>
					<Button variant="contained" onClick={() => setShowErrorModal(false)}>
						Ok
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default MembershipOptionsPage;
